import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  type AchTransferBatch as ApiAchTransferBatch,
  type AchTransferBatchListResponse as ApiAchTransferBatchListResponse,
  type TransferBatch as ApiTransferBatch,
  type TransferBatchList as ApiTransferBatchList,
  transferAchBatchListBatches,
  transferAchBatchListBatchesQueryKey,
  transferBatchListTransferBatches,
  transferBatchListTransferBatchesQueryKey,
} from '@column/api';
import { Dropdown, Icon } from '@column/column-ui-kit';
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumnDef,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  Timestamp,
} from '@column/ui';

import { RecordsHoverText } from '~/app/pages/Transfers/AchBatches/_components/RecordsHoverText';
import { ROUTE } from '~/app/routes';
import { Loading, Pagination } from '~/components';
import { LogoLoading } from '~/elements';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { TransferAmount } from '~/lib/transfers';
import { getTransferBadgeClassName } from '~/lib/transfers/utils';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { PAGE_SIZE_OPTIONS, useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatDateTime, getFormattedDate } from '~/util';

type Tab = 'check' | 'ach';

const statusToneClassName = (type?: string) => {
  if (type === 'danger' || type === 'info' || type === 'success') {
    return getTransferBadgeClassName(type);
  }
  return getTransferBadgeClassName();
};

type BatchStatusMeta = { type: string; label: string; icon: React.ReactNode };

const BATCH_STATUS_META: Record<string, BatchStatusMeta> = {
  pending: { type: 'default', label: 'Pending', icon: <Icon.CircleBlank /> },
  validating: { type: 'info', label: 'Validating', icon: <Icon.CirclePendingOne /> },
  validated: { type: 'info', label: 'Validated', icon: <Icon.CirclePendingTwo /> },
  processing: { type: 'info', label: 'Processing', icon: <Icon.CirclePendingThree /> },
  completed: { type: 'success', label: 'Completed', icon: <Icon.CircleCheck /> },
  failed: { type: 'danger', label: 'Failed', icon: <Icon.CircleCross /> },
  expired: { type: 'danger', label: 'Expired', icon: <Icon.CircleCross /> },
  canceled: { type: 'danger', label: 'Canceled', icon: <Icon.CircleCross /> },
};

const DEFAULT_BATCH_STATUS: BatchStatusMeta = {
  type: 'default',
  label: '',
  icon: <Icon.CircleBlank />,
};

const isAllInvalid = (batch: ApiAchTransferBatch): boolean =>
  batch.status === 'validated' && batch.total_count > 0 && batch.validation_failed_count === batch.total_count;

const isAllFailed = (batch: ApiAchTransferBatch): boolean =>
  batch.status === 'completed' && batch.failed_count > 0 && batch.succeeded_count === 0;

const getBatchStatusDisplay = (batch: ApiAchTransferBatch): BatchStatusMeta => {
  if (isAllInvalid(batch)) return { type: 'danger', label: 'All Invalid', icon: <Icon.CircleCross /> };
  if (isAllFailed(batch)) return { type: 'danger', label: 'All Failed', icon: <Icon.CircleCross /> };
  return BATCH_STATUS_META[batch.status] ?? { ...DEFAULT_BATCH_STATUS, label: batch.status };
};

const isPostCreation = (batch: ApiAchTransferBatch): boolean => !!batch.completed_at || batch.status === 'failed';

const buildBreakdownText = (batch: ApiAchTransferBatch): string => {
  let parts: { label: string; count: number }[] = [];
  if (isPostCreation(batch)) {
    parts = [
      { label: 'Created', count: batch.succeeded_count },
      { label: 'Failed', count: batch.failed_count },
      { label: 'Invalid', count: batch.validation_failed_count },
    ];
  } else if (batch.validated_at) {
    parts = [
      { label: 'Validated', count: batch.total_count - batch.validation_failed_count },
      { label: 'Invalid', count: batch.validation_failed_count },
    ];
  }

  return parts
    .filter((part) => part.count > 0)
    .map((part) => `${part.count} ${part.label}`)
    .join(' · ');
};

type BatchAmounts = {
  phase: 'validated' | 'created';
  credit: number;
  debit: number;
};

const pickBatchAmounts = (batch: ApiAchTransferBatch): BatchAmounts | null => {
  if (batch.status === 'canceled' || batch.status === 'expired') return null;
  if (isAllInvalid(batch) || isAllFailed(batch)) return null;
  if (isPostCreation(batch)) {
    return { phase: 'created', credit: batch.total_credit_amount, debit: batch.total_debit_amount };
  }
  if (batch.validated_at) {
    return {
      phase: 'validated',
      credit: batch.validated_total_credit_amount,
      debit: batch.validated_total_debit_amount,
    };
  }
  return null;
};

const BatchAmountCell: React.FC<{ batch: ApiAchTransferBatch }> = ({ batch }) => {
  const picked = pickBatchAmounts(batch);
  const value = picked ? picked.credit - picked.debit : null;
  return (
    <TransferAmount
      amount={value === null ? null : Math.abs(value)}
      description={batch.description}
      isAdding={value === null ? undefined : value >= 0}
    />
  );
};

type AchTransferBatchRow = ApiAchTransferBatch;

const getCheckColumns = (
  navigate: ReturnType<typeof useNavigate>,
  timeFormat: TableTimeFormat,
  onTimeFormatChange: (value: TableTimeFormat) => void
): DataTableColumnDef<ApiTransferBatch>[] => [
  {
    header: 'Amount / Description',
    accessor: 'total_amount',
    width: 'minmax(300px, 4fr)',
    className: 'min-w-0',
    cell: ({ row, value }) => (
      <TransferAmount
        amount={Math.abs(Number(value ?? 0))}
        currencyCode={row.total_currency}
        description={row.description}
        isAdding={Number(value ?? 0) >= 0}
      />
    ),
  },
  {
    header: 'Transfers',
    accessor: 'transfer_count',
    width: 'minmax(100px, 1fr)',
    cell: ({ value }) => <span className="text-muted-foreground">{value?.toString() ?? ''}</span>,
  },
  {
    header: (
      <Dropdown
        size="small"
        positionRight
        active={timeFormat}
        options={[
          { label: 'Relative Time', value: TableTimeFormat.Relative },
          { label: 'Date / Time', value: TableTimeFormat.Absolute },
        ]}
        onChange={onTimeFormatChange}
        customLabel={
          <span className="flex items-center gap-1">
            Created <Icon.ChevronDown />
          </span>
        }
      />
    ),
    accessor: 'created_at',
    width: 'min-content',
    cell: ({ row }) => (
      <Timestamp
        date={row.created_at}
        align="right"
        className="text-xs text-muted-foreground"
        formatHover={(hoverDate) =>
          formatDateTime(hoverDate, { timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc' })
        }
      >
        {timeFormat === TableTimeFormat.Relative
          ? getFormattedDate(row.created_at, 'time-passed')
          : formatDateTime(row.created_at, { timeZoneFormat: 'local' })}
      </Timestamp>
    ),
  },
  {
    header: '',
    accessor: 'id',
    width: 'minmax(140px, max-content)',
    className: 'justify-end',
    cell: ({ row }) => (
      <Button
        onClick={(event: React.SyntheticEvent) => {
          event.stopPropagation();
          navigate(`${ROUTE.TRANSFERS}/batches/${row.id}`);
        }}
        size="xs"
        variant="secondary"
        type="button"
      >
        View Batch
        <Icon.ChevronRightLarge style={{ '--icon-size': '14px' } as React.CSSProperties} />
      </Button>
    ),
  },
];

const getAchColumns = (
  navigate: ReturnType<typeof useNavigate>,
  timeFormat: TableTimeFormat,
  onTimeFormatChange: (value: TableTimeFormat) => void
): DataTableColumnDef<AchTransferBatchRow>[] => [
  {
    header: 'Amount / Description',
    accessor: 'total_credit_amount',
    width: 'minmax(300px, 4fr)',
    className: 'min-w-0',
    cell: ({ row }) => <BatchAmountCell batch={row} />,
  },
  {
    header: 'Records',
    accessor: 'total_count',
    width: 'minmax(80px, 1fr)',
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        <RecordsHoverText totalText={String(row.total_count)} breakdownText={buildBreakdownText(row)} />
      </span>
    ),
  },
  {
    header: 'Status',
    accessor: 'status',
    width: 'minmax(130px, 1fr)',
    cell: ({ row }) => {
      const meta = getBatchStatusDisplay(row);
      return (
        <Badge variant="ghost" className={statusToneClassName(meta.type)}>
          {meta.icon}
          {meta.label}
        </Badge>
      );
    },
  },
  {
    header: (
      <Dropdown
        size="small"
        positionRight
        active={timeFormat}
        options={[
          { label: 'Relative Time', value: TableTimeFormat.Relative },
          { label: 'Date / Time', value: TableTimeFormat.Absolute },
        ]}
        onChange={onTimeFormatChange}
        customLabel={
          <span className="flex items-center gap-1">
            Created <Icon.ChevronDown />
          </span>
        }
      />
    ),
    accessor: 'created_at',
    width: 'min-content',
    cell: ({ row }) => (
      <Timestamp
        date={String(row.created_at)}
        align="right"
        className="text-xs text-muted-foreground"
        formatHover={(hoverDate) =>
          formatDateTime(hoverDate, { timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc' })
        }
      >
        {timeFormat === TableTimeFormat.Relative
          ? getFormattedDate(String(row.created_at), 'time-passed')
          : formatDateTime(String(row.created_at), { timeZoneFormat: 'local' })}
      </Timestamp>
    ),
  },
  {
    header: '',
    accessor: 'id',
    width: 'minmax(140px, max-content)',
    className: 'justify-end',
    cell: ({ row }) => (
      <Button
        onClick={(event: React.SyntheticEvent) => {
          event.stopPropagation();
          navigate(`${ROUTE.TRANSFERS}/batches/ach/${row.id}`);
        }}
        size="xs"
        variant="secondary"
        type="button"
      >
        View Batch
        <Icon.ChevronRightLarge style={{ '--icon-size': '14px' } as React.CSSProperties} />
      </Button>
    ),
  },
];

const TAB_OPTIONS: { label: string; value: Tab }[] = [
  { label: 'Check Deposits', value: 'check' },
  { label: 'ACH', value: 'ach' },
];

const isTab = (v: string | null): v is Tab => v === 'check' || v === 'ach';

export const PageTransferBatchesOverview: React.FC = () => {
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  const navigate = useNavigate();
  const achEnabled = useFeatureFlag(FeatureFlag.EnableAchTransferBatches);
  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('batches');
  const { addDangerNotification } = useNotificationStore();
  const [entriesOpen, setEntriesOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: Tab = achEnabled && isTab(tabParam) ? tabParam : 'check';

  const { cursor, onNext, setPageSize, reset, ...pagination } = usePaginationState({
    defaultPageSize: pageSize,
  });

  const scopeKey = `${platformId}|${isSandbox}|${tab}`;
  const [prevScope, setPrevScope] = useState(scopeKey);
  let effectiveCursor: string | undefined = cursor;
  if (prevScope !== scopeKey) {
    setPrevScope(scopeKey);
    reset();
    effectiveCursor = undefined;
  }

  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'check') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const {
    data: checkData,
    isFetching: isCheckFetching,
    isLoading: isCheckLoading,
  } = useQuery<ApiTransferBatchList>({
    queryKey: [
      ...transferBatchListTransferBatchesQueryKey(),
      { limit: pagination.pageSize, starting_after: effectiveCursor },
    ],
    queryFn: async () => {
      const { data } = await transferBatchListTransferBatches({
        query: { limit: pagination.pageSize, starting_after: effectiveCursor },
        throwOnError: true,
      } as any);
      return data as ApiTransferBatchList;
    },
    enabled: tab === 'check',
    gcTime: 0,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

  const {
    data: achData,
    isFetching: isAchFetching,
    isLoading: isAchLoading,
  } = useQuery<ApiAchTransferBatchListResponse>({
    queryKey: [
      ...transferAchBatchListBatchesQueryKey(),
      { limit: pagination.pageSize, starting_after: effectiveCursor },
    ],
    queryFn: async () => {
      const { data } = await transferAchBatchListBatches({
        query: { limit: pagination.pageSize, starting_after: effectiveCursor },
        throwOnError: true,
      } as any);
      return data as ApiAchTransferBatchListResponse;
    },
    enabled: tab === 'ach',
    gcTime: 0,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

  const handleTimeFormatChange = useCallback(
    (value: TableTimeFormat) => {
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [addDangerNotification, updateTableConfig]
  );

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPageSize(value);
      updateTableConfig({ limit: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save page size preference' });
      });
    },
    [addDangerNotification, setPageSize, updateTableConfig]
  );

  const checkColumns = useMemo(
    () => getCheckColumns(navigate, timeFormat, handleTimeFormatChange),
    [handleTimeFormatChange, navigate, timeFormat]
  );
  const achColumns = useMemo(
    () => getAchColumns(navigate, timeFormat, handleTimeFormatChange),
    [handleTimeFormatChange, navigate, timeFormat]
  );
  const checkRows = useMemo(() => checkData?.transfer_batches ?? [], [checkData]);
  const achRows = useMemo(() => achData?.batches ?? [], [achData]);
  const activeRows = tab === 'check' ? checkRows : achRows;
  const hasNext = tab === 'check' ? (checkData?.has_more ?? false) : (achData?.has_more ?? false);
  const tableMinHeight = activeRows.length === 0 ? 341 : undefined;

  const handleNext = () => {
    const rows = tab === 'check' ? checkRows : achRows;
    const activeData = tab === 'check' ? checkData : achData;
    if (!activeData || rows.length === 0) return;
    onNext({ cursor: rows[rows.length - 1].id });
  };

  return (
    <>
      {achEnabled && (
        <div className="px-[var(--page-margin)] pb-4">
          <Tabs value={tab} onValueChange={(value) => setTab(value as Tab)}>
            <TabsList variant="line">
              {TAB_OPTIONS.map((option) => (
                <TabsTrigger key={option.value} value={option.value}>
                  {option.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}
      <Loading isLoading={isAchLoading || isCheckLoading}>
        <Inner pt={0} px="var(--page-margin)">
          {tab === 'check' && (
            <DataTable<ApiTransferBatch>
              columns={checkColumns}
              data={checkRows}
              isLoading={isCheckFetching}
              loadingContent={<LogoLoading />}
              style={{ minHeight: tableMinHeight }}
              emptyContent={
                <Empty className="min-h-[300px]">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Icon.AnimationArrows />
                    </EmptyMedia>
                    <EmptyTitle>No batches found</EmptyTitle>
                    <EmptyDescription>Deposit a batch of checks to see them here.</EmptyDescription>
                  </EmptyHeader>
                </Empty>
              }
              rowClassName="min-h-11"
              rowKey={(row) => row.id}
              onRowClick={(row) => navigate(`${ROUTE.TRANSFERS}/batches/${row.id}`)}
            />
          )}
          {tab === 'ach' && (
            <DataTable<AchTransferBatchRow>
              columns={achColumns}
              data={achRows}
              isLoading={isAchFetching}
              loadingContent={<LogoLoading />}
              style={{ minHeight: tableMinHeight }}
              emptyContent={
                <Empty className="min-h-[300px]">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Icon.AnimationArrows />
                    </EmptyMedia>
                    <EmptyTitle>No batches found</EmptyTitle>
                    <EmptyDescription>Create an ACH transfer batch tos see them here.</EmptyDescription>
                  </EmptyHeader>
                </Empty>
              }
              rowClassName="min-h-11"
              rowKey={(row) => row.id}
              onRowClick={(row) => navigate(`${ROUTE.TRANSFERS}/batches/ach/${row.id}`)}
            />
          )}

          <div className="relative z-[1] flex items-center justify-between pt-4">
            <Dropdown
              options={PAGE_SIZE_OPTIONS.map((entries: number) => ({
                label: entries.toString(),
                value: entries,
              }))}
              size="small"
              variant="muted"
              active={pagination.pageSize}
              customLabel={
                <Badge
                  variant="secondary"
                  className="rounded-[8px] [&>svg]:ml-[-4px] [&>svg]:inline-block [&>svg]:align-top"
                >
                  {pagination.pageSize}
                  {entriesOpen ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
                </Badge>
              }
              onOpenChange={setEntriesOpen}
              onChange={handlePageSizeChange}
            />
            <Pagination
              current={pagination.pageIndex + 1}
              disablePrev={!pagination.hasPrevious}
              disableNext={!hasNext}
              onPrev={pagination.onPrevious}
              onNext={handleNext}
            />
          </div>
        </Inner>
      </Loading>
    </>
  );
};
